export const privacy = {
  title: "SalesMoat Privacy Policy",
  effective: "August 16, 2026",
  updated: "August 16, 2026",
  intro: [
    "SalesMoat (\u201cSalesMoat,\u201d \u201cwe,\u201d \u201cus,\u201d or \u201cour\u201d) is operated by Cypress Group, LLC (\u201cCompany\u201d). This Privacy Policy explains how we collect, use, disclose, and protect information when you visit www.salesmoat.ai or request early access to our pilot service.",
  ],
  sections: [
    {
      heading: "Information We Collect",
      paras: ["We may collect:"],
      bullets: [
        { label: "Information you provide", text: "Name, email address, and any other details you submit when requesting early access or contacting us." },
        { label: "Automatically collected information", text: "IP address, browser type, device information, pages viewed, and usage data through cookies and analytics tools (including PostHog)." },
        { label: "Future account data (once the pilot expands)", text: "Contact and relationship information, notes, artifacts, and other Personal Sales IP that you choose to input into the Service." },
      ],
    },
    {
      heading: "How We Use Information",
      paras: ["We use the information to:"],
      bullets: [
        "Respond to early-access requests and communicate with you",
        "Operate, maintain, and improve the website and pilot Service",
        "Analyze usage and performance",
        "Comply with legal obligations",
        "Protect the security and integrity of the Service",
      ],
    },
    {
      heading: "How We Share Information",
      paras: ["We do not sell your personal information. We may share information with:"],
      bullets: [
        "Service providers who assist us (hosting, analytics, email delivery) under confidentiality obligations",
        "Legal or governmental authorities when required by law",
        "In connection with a business transfer (merger, acquisition, etc.)",
      ],
    },
    {
      heading: "Cookies and Tracking",
      paras: ["We use cookies and similar technologies for essential site functionality and analytics. You can control cookies through your browser settings."],
    },
    {
      heading: "Data Retention",
      paras: ["We retain information only as long as necessary for the purposes described above or as required by law. Pilot data may be deleted or anonymized at the end of the pilot period or upon your request."],
    },
    {
      heading: "Your Rights",
      paras: ["Depending on your location, you may have rights to access, correct, delete, or restrict processing of your personal information, or to opt out of certain uses. To exercise these rights, contact us at the email below."],
    },
    {
      heading: "Data Security",
      paras: ["We implement reasonable technical and organizational measures to protect information. However, no method of transmission or storage is 100% secure."],
    },
    {
      heading: "Children\u2019s Privacy",
      paras: ["The Service is not directed to individuals under 18. We do not knowingly collect personal information from children."],
    },
    {
      heading: "Changes to This Policy",
      paras: ["We may update this Privacy Policy from time to time. The \u201cLast Updated\u201d date will reflect changes. Continued use of the site after changes constitutes acceptance."],
    },
    {
      heading: "Contact Us",
      paras: ["Questions about this Privacy Policy may be sent to:"],
      contact: ["info@salesmoat.ai", "Cypress Group, LLC", "Texas, United States"],
    },
  ],
};

export const terms = {
  title: "SalesMoat Terms of Service",
  effective: "August 16, 2026",
  updated: "August 16, 2026",
  intro: [
    "These Terms of Service (\u201cTerms\u201d) govern your access to and use of www.salesmoat.ai and the SalesMoat pilot service (the \u201cService\u201d) operated by Cypress Group, LLC (\u201cCompany,\u201d \u201cwe,\u201d \u201cus,\u201d or \u201cour\u201d).",
    "By accessing the website, requesting early access, or using the Service, you agree to these Terms.",
  ],
  sections: [
    {
      heading: "Pilot Nature of the Service",
      paras: ["The Service is currently in pilot / early-access stage. Features, functionality, data handling, and availability may change or be discontinued at any time without notice. The Service is provided \u201cas is\u201d and \u201cas available.\u201d"],
    },
    {
      heading: "Eligibility",
      paras: ["You must be at least 18 years old and capable of entering into a binding agreement to use the Service."],
    },
    {
      heading: "Early Access and Accounts",
      paras: ["Requesting early access does not guarantee participation. If you are granted access, you are responsible for maintaining the confidentiality of any login credentials and for all activity under your account."],
    },
    {
      heading: "Your Personal Sales IP \u2013 Ownership and Responsibility",
      paras: [
        "\u201cPersonal Sales IP\u201d means the contacts, relationship history, notes, artifacts, next steps, and other relationship-management data that you input into the Service and that constitutes your personal professional relationship capital.",
        "You retain all right, title, and interest in and to your Personal Sales IP.",
        "You grant Company a limited, non-exclusive, worldwide license to host, process, and display your Personal Sales IP solely as necessary to provide the Service to you.",
        "You represent and warrant that: (a) the data you input is solely your personal relationship capital; (b) it does not include confidential information, customer lists, product information, or intellectual property belonging to any current or former employer; and (c) your use of the Service does not violate any employment, confidentiality, non-solicitation, or other agreement.",
        "Company claims no ownership of your Personal Sales IP.",
      ],
    },
    {
      heading: "Acceptable Use",
      paras: ["You agree not to:"],
      bullets: [
        "Upload or input any employer confidential information, trade secrets, or product-related intellectual property",
        "Use the Service for any unlawful purpose",
        "Attempt to reverse-engineer, scrape, or interfere with the Service",
        "Share access credentials or allow unauthorized use",
      ],
    },
    {
      heading: "Company Intellectual Property",
      paras: ["The SalesMoat platform, software, branding, design, and all related intellectual property remain the exclusive property of Company. These Terms grant you no ownership rights in the platform itself."],
    },
    {
      heading: "Disclaimers",
      paras: ["THE SERVICE IS PROVIDED \u201cAS IS\u201d WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE."],
    },
    {
      heading: "Limitation of Liability",
      paras: ["TO THE MAXIMUM EXTENT PERMITTED BY LAW, COMPANY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF DATA, PROFITS, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE."],
    },
    {
      heading: "Indemnification",
      paras: ["You agree to indemnify and hold harmless Company and its officers, directors, and agents from any claims, damages, or expenses arising out of your breach of these Terms or your misuse of the Service (including any claim that data you uploaded belongs to a third party or employer)."],
    },
    {
      heading: "Termination",
      paras: ["We may suspend or terminate your access at any time. Upon termination, your right to use the Service ends. Provisions that by their nature should survive (ownership, disclaimers, liability limits, indemnity) will survive."],
    },
    {
      heading: "Governing Law",
      paras: ["These Terms are governed by the laws of the State of Texas, without regard to conflict-of-laws principles. Any disputes shall be resolved in the state or federal courts located in Texas."],
    },
    {
      heading: "Changes",
      paras: ["We may update these Terms from time to time. Continued use after changes constitutes acceptance of the revised Terms."],
    },
    {
      heading: "Contact",
      paras: ["Questions about these Terms may be sent to:"],
      contact: ["info@salesmoat.ai", "Cypress Group, LLC", "Texas, United States"],
    },
  ],
};

export const cookies = {
  title: "SalesMoat Cookie Notice",
  effective: "August 16, 2026",
  intro: ["We use cookies and similar technologies on www.salesmoat.ai for:"],
  sections: [
    {
      bullets: [
        "Essential site functionality",
        "Analytics and performance measurement (including PostHog)",
      ],
      paras: ["You can control or delete cookies through your browser settings. Continuing to use the site indicates acceptance of our use of cookies. For more information, see our Privacy Policy."],
      contact: ["info@salesmoat.ai", "Cypress Group, LLC", "Texas, United States"],
    },
  ],
};
